import OfflineLogger from '@/utils/offlineLogger';
import { networkService } from '@/services/network.service';
import { sanitizeUrl } from '@/utils/security';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { isCapacitorPlatform } from '@/utils/platform';

class ImageCacheService {
  private cache = new Map<string, string>();
  private dbName = 'SpesaAppImageCache';
  private dbVersion = 1;
  private storeName = 'images';
  private maxCacheSize = 50 * 1024 * 1024; // 50MB limit
  private persistenceEnabled = true;
  private initializationStarted = false;

  constructor() {
    // Detect IndexedDB support. In some mobile builds the global may be missing
    // which would cause endless retries when attempting to open the DB.
    if (typeof window === 'undefined' || !('indexedDB' in window)) {
      this.persistenceEnabled = false;
      OfflineLogger.warn(
        'ImageCacheService',
        'IndexedDB not supported'
      );
    }

    this.initialize();
  }

  private initialize(): void {
    if (this.initializationStarted) return;
    this.initializationStarted = true;

    const enablePersistence = () => {
      if (!this.persistenceEnabled && typeof window !== 'undefined' && 'indexedDB' in window) {
        this.persistenceEnabled = true;
        OfflineLogger.info(
          'ImageCacheService',
          'Persistent image caching enabled'
        );
      }
    };

    // Try immediately in case IndexedDB is already available
    enablePersistence();

    const registerVisibilityFallback = () => {
      if (typeof document === 'undefined') return;

      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
          enablePersistence();
        }
      });
    };

    if (isCapacitorPlatform()) {
      const appPluginAvailable =
        typeof Capacitor.isPluginAvailable === 'function' &&
        Capacitor.isPluginAvailable('App');

      if (appPluginAvailable) {
        try {
          App.addListener('appStateChange', state => {
            if (state.isActive) {
              enablePersistence();
            }
          });
        } catch (error) {
          OfflineLogger.warn(
            'ImageCacheService',
            `Failed to register App listener, using visibilitychange fallback: ${error}`
          );
          registerVisibilityFallback();
        }
      } else {
        OfflineLogger.warn(
          'ImageCacheService',
          'Capacitor App plugin not available, using visibilitychange fallback'
        );
        registerVisibilityFallback();
      }
    } else {
      registerVisibilityFallback();
    }
  }

  async getImageUrl(productId: string, photoPath?: string, options?: { forceRefresh: boolean }): Promise<string | null> {
    if (!photoPath) return null;

    // Sanitize inputs to prevent injection attacks
    const sanitizedProductId = sanitizeUrl(productId);
    const sanitizedPhotoPath = sanitizeUrl(photoPath);
    
    if (!sanitizedProductId || !sanitizedPhotoPath) {
      OfflineLogger.error('ImageCacheService', 'Invalid product ID or photo path');
      return null;
    }

    const cacheKey = `${sanitizedProductId}-${sanitizedPhotoPath}`;
    
    // Check memory cache first
    if (this.cache.has(cacheKey) && !options?.forceRefresh) {
      return this.cache.get(cacheKey)!;
    }

    try {
      // Check IndexedDB cache
      const cachedBlob = await this.getFromIndexedDB(cacheKey);
      if (cachedBlob && !options?.forceRefresh) {
        const objectUrl = URL.createObjectURL(cachedBlob);
        this.cache.set(cacheKey, objectUrl);
        return objectUrl;
      }

      // Only try to fetch if online to avoid network errors
      if (networkService.isOnline()) {
        // Validate URL to prevent SSRF attacks
        let imageUrl: string;
        if (this.isValidImageUrl(sanitizedPhotoPath)) {
          imageUrl = sanitizedPhotoPath;
        } else {
          // Construct secure URL
          imageUrl = `https://gxnmcjgxredhctobhkki.supabase.co/storage/v1/object/public/product-photos/${encodeURIComponent(sanitizedPhotoPath)}`;
        }
        
        try {
          const response = await fetch(imageUrl, {
            // Add security headers
            headers: {
              'Cache-Control': 'max-age=3600'
            },
            // Prevent credentials from being sent
            credentials: 'omit'
          });
          
          if (response.ok) {
            const blob = await response.blob();
            
            // Validate file type
            if (!this.isValidImageType(blob.type)) {
              OfflineLogger.error('ImageCacheService', `Invalid image type: ${blob.type}`);
              return null;
            }
            
            // Check file size
            if (blob.size > 10 * 1024 * 1024) { // 10MB limit
              OfflineLogger.error('ImageCacheService', `Image too large: ${blob.size} bytes`);
              return null;
            }
            
            // Cache the blob
            await this.saveToIndexedDB(cacheKey, blob);
            
            // Create object URL and cache in memory
            const objectUrl = URL.createObjectURL(blob);
            this.cache.set(cacheKey, objectUrl);
            
            return objectUrl;
          } else {
            OfflineLogger.info('ImageCacheService', `Failed to fetch image: ${response.status} for ${imageUrl}`);
          }
        } catch (fetchError) {
          // Don't log network errors when offline as they're expected
          if (networkService.isOnline()) {
            OfflineLogger.error('ImageCacheService', `Failed to fetch image: ${fetchError}`);
          }
        }
      }

      return null;
    } catch (error) {
      OfflineLogger.error('ImageCacheService', `Error getting image: ${error}`);
      return null;
    }
  }

  private isValidImageUrl(url: string): boolean {
    try {
      const parsed = new URL(url);
      // Only allow HTTPS URLs from trusted domains
      return parsed.protocol === 'https:' && 
             (parsed.hostname.endsWith('.supabase.co') || 
              parsed.hostname.endsWith('.githubusercontent.com'));
    } catch {
      return false;
    }
  }

  private isValidImageType(mimeType: string): boolean {
    const allowedTypes = [
      'image/jpeg',
      'image/png',
      'image/gif',
      'image/webp',
      'image/svg+xml'
    ];
    return allowedTypes.includes(mimeType);
  }

  invalidateImage(productId: string): void {
    const sanitizedProductId = sanitizeUrl(productId);
    if (!sanitizedProductId) return;

    // Remove from memory cache
    const keysToRemove = Array.from(this.cache.keys()).filter(key => 
      key.startsWith(`${sanitizedProductId}-`)
    );
    
    keysToRemove.forEach(key => {
      const url = this.cache.get(key);
      if (url) {
        URL.revokeObjectURL(url);
        this.cache.delete(key);
      }
    });
  }

  cleanExpiredEntries(): void {
    // Clean memory cache and check size limits
    const cacheSize = this.cache.size;
    if (cacheSize > 100) { // Limit memory cache entries
      const entries = Array.from(this.cache.entries());
      const toRemove = entries.slice(0, cacheSize - 100);
      
      toRemove.forEach(([key, url]) => {
        URL.revokeObjectURL(url);
        this.cache.delete(key);
      });
    }
    
    OfflineLogger.info('ImageCacheService', 'Cleaning expired entries from memory cache');
  }

  private async getFromIndexedDB(key: string): Promise<Blob | null> {
    if (!this.initializationStarted) {
      this.initialize();
    }

    if (!this.persistenceEnabled) {
      return Promise.resolve(null);
    }

    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.dbVersion);
      
      request.onerror = () => reject(request.error);
      
      request.onsuccess = () => {
        const db = request.result;
        const transaction = db.transaction([this.storeName], 'readonly');
        const store = transaction.objectStore(this.storeName);
        const getRequest = store.get(key);
        
        getRequest.onsuccess = () => {
          const result = getRequest.result;
          if (result && result.blob && result.timestamp) {
            // Check if entry is expired (30 days)
            const maxAge = 30 * 24 * 60 * 60 * 1000;
            if (Date.now() - result.timestamp < maxAge) {
              resolve(result.blob);
            } else {
              resolve(null);
            }
          } else {
            resolve(null);
          }
        };
        
        getRequest.onerror = () => reject(getRequest.error);
      };
      
      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains(this.storeName)) {
          db.createObjectStore(this.storeName);
        }
      };
    });
  }

  private async saveToIndexedDB(key: string, blob: Blob): Promise<void> {
    if (!this.initializationStarted) {
      this.initialize();
    }

    if (!this.persistenceEnabled) {
      return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.dbVersion);
      
      request.onerror = () => reject(request.error);
      
      request.onsuccess = () => {
        const db = request.result;
        const transaction = db.transaction([this.storeName], 'readwrite');
        const store = transaction.objectStore(this.storeName);
        const putRequest = store.put({ blob, timestamp: Date.now() }, key);
        
        putRequest.onsuccess = () => resolve();
        putRequest.onerror = () => reject(putRequest.error);
      };
      
      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains(this.storeName)) {
          db.createObjectStore(this.storeName);
        }
      };
    });
  }

  /**
   * Check if a product image already exists in the cache.
   *
   * The lookup is performed first in the in‑memory map and then in
   * the IndexedDB store used for persistent caching.
   */
  async isCached(productId: string, photoPath: string): Promise<boolean> {
    const sanitizedId = sanitizeUrl(productId);
    const sanitizedPhoto = sanitizeUrl(photoPath);

    if (!sanitizedId || !sanitizedPhoto) return false;

    const cacheKey = `${sanitizedId}-${sanitizedPhoto}`;

    if (this.cache.has(cacheKey)) return true;

    const cachedBlob = await this.getFromIndexedDB(cacheKey);
    return !!cachedBlob;
  }

  async preloadImages(
    productPhotos: Array<{ id: string; photo?: string }>,
    onProgress?: (downloaded: number, total: number) => void
  ): Promise<void> {
    // Check online status and validate inputs
    if (!networkService.isOnline()) {
      OfflineLogger.info('ImageCacheService', 'Skipping image preload - offline mode');
      return;
    }

    // Sanitize and validate inputs
    const validPhotos = productPhotos.filter(p => {
      if (!p.photo || !p.id) return false;
      
      const sanitizedId = sanitizeUrl(p.id);
      const sanitizedPhoto = sanitizeUrl(p.photo);
      
      if (!sanitizedId || !sanitizedPhoto) return false;
      
      // Skip if URL looks malformed
      if (sanitizedPhoto.includes('product-photos/https://')) return false;
      
      return true;
    });

    const missingPhotos: Array<{ id: string; photo: string }> = [];
    for (const p of validPhotos) {
      if (!(await this.isCached(p.id, p.photo))) {
        missingPhotos.push({ id: p.id, photo: p.photo! });
      }
    }

    const total = missingPhotos.length;
    onProgress?.(0, total);

    // Limit concurrent requests
    const batchSize = 5;
    let downloaded = 0;
    for (let i = 0; i < missingPhotos.length; i += batchSize) {
      const batch = missingPhotos.slice(i, i + batchSize);
      const promises = batch.map(async p => {
        await this.getImageUrl(p.id, p.photo);
        downloaded++;
        onProgress?.(downloaded, total);
      });

      try {
        await Promise.allSettled(promises);
      } catch (error) {
        OfflineLogger.error('ImageCacheService', `Error preloading batch: ${error}`);
      }
    }

    OfflineLogger.info('ImageCacheService', `Preloaded ${missingPhotos.length} images`);
  }

  clearMemoryCache(): void {
    // Revoke object URLs to free memory
    this.cache.forEach(url => URL.revokeObjectURL(url));
    this.cache.clear();
  }

  async clearPersistentCache(): Promise<void> {
    // Always clear memory cache as well
    this.clearMemoryCache();

    if (!this.persistenceEnabled) {
      return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
      const deleteRequest = indexedDB.deleteDatabase(this.dbName);

      deleteRequest.onsuccess = () => {
        this.initializationStarted = false;
        resolve();
      };
      deleteRequest.onerror = () => reject(deleteRequest.error);
      deleteRequest.onblocked = () => resolve();
    });
  }
}

export const imageCache = new ImageCacheService();
